// web/src/pages/invoices/NewInvoiceWizard.tsx
//
// I-3 — 3-step "New invoice" wizard (Customer → Lines → Review).
//
// Plan III (Task 24) adds ?jobId= support:
//   - When ?jobId=N is present, the wizard fetches jobsApi.items(N) to
//     pre-populate lines and calls createInvoiceFromJob on submit.
//   - The step-2 header changes to "From Job: <jobReferenceNo>".
//   - All other behaviour is unchanged when ?jobId is absent.
//
// The backend's RazorInvoiceComposer returns authoritative totals on POST
// /api/invoices, so the Review step does NOT preview totals locally. It just
// shows the line array.
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ClientPicker, type ClientPickerValue } from "@/components/client-picker";
import { AccountPicker } from "@/components/account-picker";
import { PageContainer } from "@/app/layouts/PageContainer";
import { IntentButton } from "@/components/intent-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  createInvoice,
  createInvoiceFromJob,
  type CreateInvoiceRequest,
  type CreateInvoiceLineItemRequest,
} from "@/lib/invoices-payments";
import { jobsApi } from "@/lib/jobs";

interface LineDraft {
  description: string;
  quantity: string;
  unitPriceExGst: string;
  discountPct: string;
  gstApplicable: boolean;
  isCredit: boolean;
}

const BLANK_LINE: LineDraft = {
  description: "",
  quantity: "1",
  unitPriceExGst: "0",
  discountPct: "0",
  gstApplicable: true,
  isCredit: false,
};

function todayPlus30Iso(): string {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
}

function validateLine(line: LineDraft): boolean {
  if (line.description.trim().length === 0) return false;
  const q = Number(line.quantity);
  const p = Number(line.unitPriceExGst);
  const d = Number(line.discountPct);
  if (!Number.isFinite(q) || q <= 0) return false;
  if (!Number.isFinite(p) || p < 0) return false;
  if (!Number.isFinite(d) || d < 0 || d > 100) return false;
  return true;
}

function toApiLine(line: LineDraft, sortOrder: number): CreateInvoiceLineItemRequest {
  return {
    description: line.description,
    quantity: Number(line.quantity),
    unitPriceExGst: Number(line.unitPriceExGst),
    discountPct: Number(line.discountPct),
    gstApplicable: line.gstApplicable,
    isCredit: line.isCredit,
    jobDetailId: null,
    sortOrder,
  };
}

export function NewInvoiceWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jobIdParam = searchParams.get("jobId");
  const jobId = jobIdParam !== null ? Number(jobIdParam) : null;

  const [step, setStep] = useState<0 | 1 | 2>(0);

  // Step 1: customer
  const [client, setClient] = useState<ClientPickerValue | null>(null);
  const [accountId, setAccountId] = useState<number | null>(null);
  const [addressIdInput, setAddressIdInput] = useState("");

  // Step 2: lines
  const [lines, setLines] = useState<LineDraft[]>([{ ...BLANK_LINE }]);

  // Step 3: review
  const [dueDate, setDueDate] = useState<string>(todayPlus30Iso());
  const [comment, setComment] = useState<string>("");
  const [deliveryChargeExGst, setDeliveryChargeExGst] = useState<string>("");
  const [submitError, setSubmitError] = useState<string | null>(null);

  // When jobId is supplied, fetch job detail (for jobReferenceNo in heading)
  // and job items (to pre-populate lines).
  const jobDetailQuery = useQuery({
    queryKey: ["job", jobId],
    queryFn: () => jobsApi.get(jobId!),
    enabled: jobId !== null,
  });

  const jobItemsQuery = useQuery({
    queryKey: ["job-items", jobId],
    queryFn: () => jobsApi.items(jobId!),
    enabled: jobId !== null,
  });

  // Once job items arrive, seed the lines state (runs once when data lands).
  useEffect(() => {
    if (!jobItemsQuery.data) return;
    const seeded = jobItemsQuery.data.map((item) => ({
      description: item.description,
      quantity: String(item.quantity),
      unitPriceExGst: String(item.unitPriceExGst),
      discountPct: "0",
      gstApplicable: item.gstRate > 0,
      isCredit: false,
    }));
    setLines(seeded.length > 0 ? seeded : [{ ...BLANK_LINE }]);
  }, [jobItemsQuery.data]);

  const linesValid = useMemo(
    () => lines.length > 0 && lines.every(validateLine),
    [lines],
  );

  // When invoked from a job, use createInvoiceFromJob; otherwise createInvoice.
  const createFromJobMut = useMutation({
    mutationFn: () => createInvoiceFromJob(jobId!),
    onSuccess: (inv) => {
      navigate(`/invoices/${inv.invoiceId}`);
    },
    onError: (e: Error) => setSubmitError(e.message),
  });

  const createMut = useMutation({
    mutationFn: (body: CreateInvoiceRequest) => createInvoice(body),
    onSuccess: (inv) => {
      navigate(`/invoices/${inv.invoiceId}`);
    },
    onError: (e: Error) => setSubmitError(e.message),
  });

  const isPending = jobId !== null ? createFromJobMut.isPending : createMut.isPending;

  function buildPayload(): CreateInvoiceRequest {
    const addrId = addressIdInput.trim() === "" ? null : Number(addressIdInput);
    const delivery = deliveryChargeExGst.trim() === ""
      ? null
      : Number(deliveryChargeExGst);
    return {
      clientId: client!.clientId,
      accountId,
      jobId: null,
      addressId: addrId !== null && Number.isFinite(addrId) ? addrId : null,
      deliveryChargeExGst: delivery !== null && Number.isFinite(delivery) ? delivery : null,
      lines: lines.map((l, i) => toApiLine(l, i)),
      commentToShowOnInvoice: comment.trim() === "" ? null : comment,
      dueDate: dueDate || null,
    };
  }

  function onSave() {
    setSubmitError(null);
    if (jobId !== null) {
      createFromJobMut.mutate();
    } else {
      createMut.mutate(buildPayload());
    }
  }

  const linesStepHeader = jobId !== null && jobDetailQuery.data
    ? `From Job: ${jobDetailQuery.data.jobReferenceNo}`
    : "Enter line items";

  return (
    <PageContainer>
      <h1 className="mb-1 text-2xl font-semibold">New invoice</h1>
      <ol className="mb-6 flex gap-3 text-sm">
        <li className={step === 0 ? "font-semibold" : "text-muted-foreground"}>1. Customer</li>
        <li className={step === 1 ? "font-semibold" : "text-muted-foreground"}>2. Lines</li>
        <li className={step === 2 ? "font-semibold" : "text-muted-foreground"}>3. Review</li>
      </ol>

      {step === 0 && (
        <section aria-label="Step 1: Customer" className="space-y-4 max-w-xl">
          <div>
            <Label>Client</Label>
            <ClientPicker value={client} onChange={setClient} />
          </div>
          <div>
            <Label>Account (optional)</Label>
            <AccountPicker
              clientId={client?.clientId}
              value={accountId}
              onChange={setAccountId}
              placeholder={client ? "Search accounts..." : "Select a client first"}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Leave unset for direct-bill invoices.
            </p>
          </div>
          <div>
            <Label htmlFor="addr">Address ID (optional override)</Label>
            <Input
              id="addr"
              type="number"
              value={addressIdInput}
              onChange={(e) => setAddressIdInput(e.target.value)}
            />
          </div>
          <div className="flex justify-end">
            <IntentButton disabled={client === null} onClick={() => setStep(1)}>
              Next
            </IntentButton>
          </div>
        </section>
      )}

      {step === 1 && (
        <section aria-label={`Step 2: ${linesStepHeader}`} className="space-y-4">
          <h2 className="text-lg font-semibold">{linesStepHeader}</h2>
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="p-2 text-left">Description</th>
                  <th className="p-2 text-right">Qty</th>
                  <th className="p-2 text-right">Unit ex GST</th>
                  <th className="p-2 text-right">Discount %</th>
                  <th className="p-2 text-center">GST?</th>
                  <th className="p-2 text-center">Credit?</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} className="border-t">
                    <td className="p-1">
                      <Input
                        aria-label={`Description ${i + 1}`}
                        value={l.description}
                        onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, description: e.target.value } : x))}
                      />
                    </td>
                    <td className="p-1">
                      <Input
                        aria-label={`Quantity ${i + 1}`}
                        type="number"
                        value={l.quantity}
                        onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, quantity: e.target.value } : x))}
                      />
                    </td>
                    <td className="p-1">
                      <Input
                        aria-label={`Unit price ${i + 1}`}
                        type="number"
                        value={l.unitPriceExGst}
                        onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, unitPriceExGst: e.target.value } : x))}
                      />
                    </td>
                    <td className="p-1">
                      <Input
                        aria-label={`Discount % ${i + 1}`}
                        type="number"
                        value={l.discountPct}
                        onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, discountPct: e.target.value } : x))}
                      />
                    </td>
                    <td className="p-1 text-center">
                      <input
                        aria-label={`GST applicable ${i + 1}`}
                        type="checkbox"
                        checked={l.gstApplicable}
                        onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, gstApplicable: e.target.checked } : x))}
                      />
                    </td>
                    <td className="p-1 text-center">
                      <input
                        aria-label={`Is credit ${i + 1}`}
                        type="checkbox"
                        checked={l.isCredit}
                        onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, isCredit: e.target.checked } : x))}
                      />
                    </td>
                    <td className="p-1 text-center">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={lines.length === 1}
                        onClick={() => setLines((prev) => prev.filter((_, j) => j !== i))}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button type="button" variant="outline" onClick={() => setLines((prev) => [...prev, { ...BLANK_LINE }])}>
            Add line
          </Button>
          <div className="flex justify-between">
            <IntentButton intent="ghost" onClick={() => setStep(0)}>Back</IntentButton>
            <IntentButton disabled={!linesValid} onClick={() => setStep(2)}>Next</IntentButton>
          </div>
        </section>
      )}

      {step === 2 && (
        <section aria-label="Step 3: Review" className="space-y-4 max-w-2xl">
          <div className="rounded-md border p-3">
            <h2 className="mb-2 font-semibold">Summary</h2>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <dt className="text-muted-foreground">Client</dt>
              <dd>{client?.fullName ?? "—"}</dd>
              <dt className="text-muted-foreground">Account ID</dt>
              <dd>{accountId != null ? String(accountId) : "(direct-bill)"}</dd>
              <dt className="text-muted-foreground">Address ID</dt>
              <dd>{addressIdInput || "(default)"}</dd>
              <dt className="text-muted-foreground">Lines</dt>
              <dd>{lines.length}</dd>
            </dl>
            <table className="mt-3 w-full text-xs">
              <thead><tr><th className="text-left">Description</th><th className="text-right">Qty</th><th className="text-right">Unit</th><th className="text-right">Disc %</th></tr></thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i}>
                    <td>{l.description}</td>
                    <td className="text-right">{l.quantity}</td>
                    <td className="text-right">{l.unitPriceExGst}</td>
                    <td className="text-right">{l.discountPct}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-xs text-muted-foreground">
              Totals (subtotal, GST, total) will be computed authoritatively by the server on save.
            </p>
          </div>
          <div>
            <Label htmlFor="due">Due date</Label>
            <Input id="due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="cmt">Comment to show on invoice</Label>
            <textarea
              id="cmt"
              className="block w-full rounded-md border p-2 text-sm"
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="dlv">Delivery charge ex GST (optional)</Label>
            <Input
              id="dlv"
              type="number"
              value={deliveryChargeExGst}
              onChange={(e) => setDeliveryChargeExGst(e.target.value)}
            />
          </div>
          {submitError && <p className="text-sm text-destructive">{submitError}</p>}
          <div className="flex justify-between">
            <IntentButton intent="ghost" onClick={() => setStep(1)}>Back</IntentButton>
            <IntentButton onClick={onSave} loading={isPending}>Save as draft</IntentButton>
          </div>
        </section>
      )}
    </PageContainer>
  );
}
